import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { batchPaymentStatus } from "@/api/client";
import { useSession } from "@/auth/session";
import { ErrorBanner } from "@/components/ui";

// The full list lives on the payments screen; this panel only hints at who is missing.
const UNPAID_SHOWN = 5;

export function BatchPaymentsPanel({ batchId }: { batchId: string }) {
  const { can } = useSession();
  const status = useQuery({ queryKey: ["batch-payments", batchId], queryFn: () => batchPaymentStatus(batchId) });
  const data = status.data;

  return (
    <section className="space-y-3 rounded-md border border-zinc-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Payments</h3>
          {data ? (
            <p className="mt-1 text-xs text-zinc-500">
              {data.requires_payment ? "Payment required" : "Not required for this program"}
            </p>
          ) : null}
        </div>
        <Link to="/payments" search={{ batch: batchId }} className="text-sm font-medium text-zinc-950 underline">
          {can("payment.record") ? "Record payment" : "View payments"}
        </Link>
      </div>
      <ErrorBanner error={status.error} />
      {data ? (
        <>
          <p className="text-sm font-medium text-zinc-900">
            {data.paid} of {data.total} recipients paid
          </p>
          {data.batch_payment_confirmed ? (
            <p className="text-xs text-emerald-700">A confirmed batch-level payment covers everyone.</p>
          ) : null}
          {data.unpaid.length > 0 ? (
            <ul className="space-y-1 text-xs text-zinc-700">
              {data.unpaid.slice(0, UNPAID_SHOWN).map((row) => (
                <li key={row.row}>
                  Row {row.row}: {row.full_name}
                  {row.membership_no ? <span className="ml-1 font-mono text-zinc-500">{row.membership_no}</span> : null}
                </li>
              ))}
              {data.unpaid.length > UNPAID_SHOWN ? (
                <li className="text-zinc-500">and {data.unpaid.length - UNPAID_SHOWN} more</li>
              ) : null}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
